/**
 * Rascunho do wizard do `signup-id` para o "Voltar" da tela de senha.
 *
 * O wizard é state local; o `edit_identifier` do Auth0 recarrega a tela e o
 * apagaria. O passo 3 grava o rascunho antes de chamar o Auth0, a tela de
 * senha deixa um marcador de retorno antes de navegar, e o `signup-id` só
 * restaura quando encontra o marcador — sem ele, o rascunho é descartado
 * para não pré-preencher um cadastro novo com dados de outro na mesma aba.
 *
 * `sessionStorage`, mesma aba, morre com ela — igual ao token e ao perfil.
 */

const DRAFT_KEY = 'alelo_signup_draft'
const RETURN_KEY = 'alelo_signup_return'

export type SignupDraft = {
  cpf: string
  email: string
  phone: string
  givenName: string
  familyName: string
  dateOfBirth: string
  gender: string
  motherName: string
  /** 'no' quando o nome da mãe ficou em branco. */
  hasMotherName: string
}

const FIELDS: Array<keyof SignupDraft> = [
  'cpf',
  'email',
  'phone',
  'givenName',
  'familyName',
  'dateOfBirth',
  'gender',
  'motherName',
  'hasMotherName',
]

export function save(draft: SignupDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Storage indisponível: o "Voltar" da tela de senha abre o wizard vazio.
  }
}

/** A tela de senha marca o retorno logo antes de navegar para o `signup-id`. */
export function markReturn(): void {
  try {
    sessionStorage.setItem(RETURN_KEY, '1')
  } catch {
    // Sem marcador o wizard abre vazio; nada a fazer.
  }
}

/**
 * No mount do `signup-id`: com marcador e rascunho válido devolve o rascunho;
 * em qualquer outro caso devolve `null`. Marcador e rascunho são consumidos
 * sempre — o próximo passo 3 grava um rascunho novo.
 */
export function restore(): SignupDraft | null {
  let returning = false
  let raw: string | null = null
  try {
    returning = sessionStorage.getItem(RETURN_KEY) === '1'
    raw = sessionStorage.getItem(DRAFT_KEY)
    sessionStorage.removeItem(RETURN_KEY)
    sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    return null
  }
  if (!returning || !raw) return null

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const draft = {} as SignupDraft
    for (const field of FIELDS) {
      const value = parsed[field]
      draft[field] = typeof value === 'string' ? value : ''
    }
    return draft
  } catch {
    return null
  }
}

export function clear(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY)
    sessionStorage.removeItem(RETURN_KEY)
  } catch {
    // nada a fazer
  }
}
